// Annual Vendor Audit — TanStack Query hooks over vendorAuditsApi.
//
// EVERY mutation invalidates `vendorAuditKeys.all`, and that is not laziness:
// one write genuinely moves the detail, the year's list, the completeness
// denominator on the scope read and the vendor's history strip. Invalidating
// the root is the only option that cannot leave one of the four behind, and
// React Query keeps the previous data while refetching, so no surface blanks.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { vendorAuditsApi } from "@/api/portfolioAdmin/vendorAuditsApi";
import type {
  AddVendorAuditQuoteRequest,
  CompleteVendorAuditRequest,
  UpdateVendorAuditDraftRequest,
  UpdateVendorAuditQuoteRequest,
  VendorAuditDetailDto,
  VendorAuditListParams,
  VendorAuditScopeDto,
  VendorAuditSummaryDto,
} from "@/api/portfolioAdmin/vendorAuditsApi";
import { vendorAuditKeys } from "./vendorAuditKeys";

// ── reads ────────────────────────────────────────────────────────────────

export function useVendorAudits(
  params?: VendorAuditListParams,
  options?: { enabled?: boolean },
) {
  return useQuery<VendorAuditSummaryDto[]>({
    queryKey: vendorAuditKeys.list(params),
    queryFn: () => vendorAuditsApi.list(params),
    enabled: options?.enabled ?? true,
    // A vendor commenting from their portal never reaches the staff query
    // client — the portal uses no React Query and there is no push channel.
    // With the global 5-minute staleTime and refetchOnWindowFocus off, a staff
    // tab left open showed a comment count that could not recover short of a
    // reload. (The same reasoning as useVendorReports.)
    refetchOnWindowFocus: true,
  });
}

/**
 * Pass `year` undefined to let the SERVER resolve the current audit year.
 *
 * The server uses the Phoenix calendar year; a client-derived `new
 * Date().getFullYear()` disagrees with it for seven hours every 1 January, and
 * would then key the scope read and the list read to different years — the
 * completeness headline describing a set the table below it does not show.
 */
export function useVendorAuditScope(year?: number) {
  return useQuery<VendorAuditScopeDto>({
    queryKey: vendorAuditKeys.scope(year),
    queryFn: () => vendorAuditsApi.scope(year),
    refetchOnWindowFocus: true,
  });
}

export function useVendorAudit(auditId: string | undefined) {
  return useQuery<VendorAuditDetailDto>({
    queryKey: vendorAuditKeys.detail(auditId ?? ""),
    queryFn: () => vendorAuditsApi.get(auditId!),
    enabled: !!auditId,
    refetchOnWindowFocus: true,
  });
}

/** Every audit ever run for one vendor, newest year first — the record's
 *  history strip, including superseded corrections. */
export function useVendorAuditsForVendor(
  vendorId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<VendorAuditSummaryDto[]>({
    queryKey: vendorAuditKeys.forVendor(vendorId ?? ""),
    queryFn: () => vendorAuditsApi.listForVendor(vendorId!),
    enabled: !!vendorId && (options?.enabled ?? true),
    refetchOnWindowFocus: true,
  });
}

// ── writes ───────────────────────────────────────────────────────────────

function useInvalidateVendorAudits() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: vendorAuditKeys.all });
}

export function useCreateVendorAudit() {
  const invalidate = useInvalidateVendorAudits();
  return useMutation({
    mutationFn: (body: { vendorId: string; auditYear: number }) =>
      vendorAuditsApi.create(body),
    onSuccess: invalidate,
  });
}

export function useUpdateVendorAuditDraft(auditId: string) {
  const invalidate = useInvalidateVendorAudits();
  return useMutation({
    mutationFn: (body: UpdateVendorAuditDraftRequest) =>
      vendorAuditsApi.updateDraft(auditId, body),
    onSuccess: invalidate,
  });
}

export function useAddVendorAuditQuote(auditId: string) {
  const invalidate = useInvalidateVendorAudits();
  return useMutation({
    mutationFn: (body: AddVendorAuditQuoteRequest) =>
      vendorAuditsApi.addQuote(auditId, body),
    onSuccess: invalidate,
  });
}

export function useUpdateVendorAuditQuote(auditId: string) {
  const invalidate = useInvalidateVendorAudits();
  return useMutation({
    mutationFn: (vars: {
      quoteId: string;
      body: UpdateVendorAuditQuoteRequest;
    }) => vendorAuditsApi.updateQuote(auditId, vars.quoteId, vars.body),
    onSuccess: invalidate,
  });
}

export function useRemoveVendorAuditQuote(auditId: string) {
  const invalidate = useInvalidateVendorAudits();
  return useMutation({
    mutationFn: (quoteId: string) =>
      vendorAuditsApi.removeQuote(auditId, quoteId),
    onSuccess: invalidate,
  });
}

export function useReleaseVendorAudit(auditId: string) {
  const invalidate = useInvalidateVendorAudits();
  return useMutation({
    mutationFn: (body: { reviewWindowDays?: number }) =>
      vendorAuditsApi.release(auditId, body),
    onSuccess: invalidate,
  });
}

export function useCloseVendorAuditReview(auditId: string) {
  const invalidate = useInvalidateVendorAudits();
  return useMutation({
    mutationFn: () => vendorAuditsApi.closeReview(auditId),
    onSuccess: invalidate,
  });
}

export function useCompleteVendorAudit(auditId: string) {
  const invalidate = useInvalidateVendorAudits();
  return useMutation({
    mutationFn: (body: CompleteVendorAuditRequest) =>
      vendorAuditsApi.complete(auditId, body),
    onSuccess: invalidate,
  });
}

/** Retires a completed audit and opens its replacement draft. One-way — the
 *  completed row is never edited in place. */
export function useSupersedeVendorAudit(auditId: string) {
  const invalidate = useInvalidateVendorAudits();
  return useMutation({
    mutationFn: () => vendorAuditsApi.supersede(auditId),
    onSuccess: invalidate,
  });
}

export function useAddVendorAuditComment(auditId: string) {
  const invalidate = useInvalidateVendorAudits();
  return useMutation({
    mutationFn: (body: { body: string }) =>
      vendorAuditsApi.addComment(auditId, body),
    onSuccess: invalidate,
  });
}
